import moment from 'moment'
import Utils, { Dependencies } from './utils.js'
import ApiError from './api-error.js'
import EventBus from '@/components/mixins/event-bus-mixin'
import { isRef, unref } from 'vue'
import CONSTANTS from '@/globals/constants';
import { useMixpanel } from '@base-shared/composables/use-mixpanel'
import { useCognitoAuthStore } from '@base-shared/stores/cognito-auth';

/**
 * signedInUserDetails dependency
 * @type {import('vue').Ref}
 */
let signedInUserDetails = null

export default {
  /**
   * Getter for api instance dependency
   * @returns {import('axios').AxiosInstance}
   */
  getApiInstance () {
    const $api = Dependencies.inject('$api');

    if (!$api) {
      throw new Error('API Instance was not instantiated')
    }

    return $api
  },

  /**
   * Setter for signedInUserDetails dependency
   *
   * @param {import('vue').Ref} setSignedInUserDetailsRef
   */
  setSignedInUserDetails (setSignedInUserDetailsRef) {
    signedInUserDetails = setSignedInUserDetailsRef ?? null
  },
  /**
   * Getter for signedInUserDetails dependency
   * Since signedInUserDetails from main is now a Vue.ref
   * validate if isRef then perform unref when returning
   *
   * @returns {any} signedInUserDetails
   */
  getSignedInUserDetails () {
    if (isRef(signedInUserDetails)) {
      return unref(signedInUserDetails)
    }

    return signedInUserDetails
  },

  /**
   * User Rights object model
   */
  userRightsModel () {
    return {
      userCreateLock: false,
      userCreate: {
        createTemplateSetStyle: false,
        editTemplateSetStyle: false,
        createNewProject: false,
        createCustomProject: false,
        deleteMedia: false,
        editProject: false,
        editMediaDuration: false,
        editMediaName: false,
        uploadMedia: false,
        displayTags: false,
        editTags: false,
        viewTemplateSets: false
      },
      userPlayer: {
        editPlayer: false,
        deletePlayer: false,
        setupPlayer: false,
        enablePlayersTags: false
      },
      userSchedule: {
        editCalendar: false,
        changeTarget: false,
        setTarget: false
      },
      userAlert: {
        createAlert: false
      }
    }
  },
  /**
    * Get Cognito Groups from user details payload and return list of user groups
    */
  getUserSessionFromLocalstorage () {
    return useCognitoAuthStore().authSession?.tokens
  },
  /**
   * Get Account Features from user session payload and return list of features in plain object
  */
  getAccountFeatures () {
    const signInUserSession = this.getUserSessionFromLocalstorage()

    if (!signInUserSession?.idToken?.payload?.account_features) {
      return {}
    }

    return JSON.parse(signInUserSession.idToken.payload.account_features)
  },
  /**
   * Get Cognito Groups from user session payload and return list of user groups
  */
  getUserGroups () {
    const signInUserSession = this.getUserSessionFromLocalstorage()

    if (!signInUserSession?.idToken?.payload?.['cognito:groups']) {
      return []
    }

    return signInUserSession.idToken.payload['cognito:groups']
  },
  /**
    * Get Cognito Group Id from user session
  */

  /**
   * Get Cognito Group Id from user session. If no `custom:group_id`
   * is found, use the `preferred username`, or if not present, use
   * `sub`. For most users `preferred_username` and sub are always
   * equal, but that is no longer the case when users are moved from
   * one region to another, so it is important to have the fallbacks
   * in the proper order.
   *
   * @returns {string | null} The group ID if available, otherwise null.
   */
  getUserGroupId () {
    const signInUserSession = this.getUserSessionFromLocalstorage()
    const sessionIdToken = signInUserSession?.idToken?.payload;

    if (!sessionIdToken) return null;

    const sessionGroupId = sessionIdToken['custom:group_id'] ??
      sessionIdToken['preferred_username'] ??
      sessionIdToken['sub'];

    return typeof sessionGroupId === 'string' && sessionGroupId.trim()?.length
      ? sessionGroupId
      : null;
  },
  getUserTags () {
    const signInUserSession = this.getUserSessionFromLocalstorage()

    if (!signInUserSession?.idToken?.payload?.['custom:tags']) return []

    return JSON.parse(signInUserSession.idToken.payload['custom:tags'])
  },
  /**
   * Handle User Rights based on the given groups
  */
  getRights () {
    const userRights = this.userRightsModel()

    const enableAllFeatures = (params) => {
      for (const indx in userRights) {
        const userContractGroup = userRights[indx]

        if (indx === 'userCreateLock') {
          userRights[indx] = false
        } else {
          for (const ucgIndx in userContractGroup) {
            userContractGroup[ucgIndx] = true
          }
        }
      }
    }

    /**
     * STAND ALONE OR FREE USER CONTRACT
     */
    if (this.isStandAloneUser() || this.isFreeUser()) {
      for (const indx in userRights) {
        const userContractGroup = userRights[indx]

        if (indx === 'userCreateLock') {
          userRights[indx] = true
        } else {
          for (const ucgIndx in userContractGroup) {
            userContractGroup[ucgIndx] = true

            if (ucgIndx === 'createCustomProject') {
              userContractGroup[ucgIndx] = false
            }

            if (ucgIndx === 'editTags') {
              userContractGroup[ucgIndx] = false
            }

            if (ucgIndx === 'displayTags') {
              userContractGroup[ucgIndx] = false
            }
          }
        }
      }
      return userRights
    }

    /**
     * ADMIN USER CONTRACT
     */
    if (this.hasGroupUserAdmin()) {
      enableAllFeatures()
      return userRights
    }

    /**
     * SUB USERS
     * Enable rights based on the gived user group
     */
    if (this.getUserGroupId() !== null && !this.hasGroupUserAdmin()) {
      userRights.userCreate.displayTags = true

      // User has user_create group
      if (this.hasGroupUserCreate()) {
        for (const ucIndx in userRights.userCreate) {
          userRights.userCreate[ucIndx] = true
        }
      }

      // User has user_player group
      if (this.hasGroupUserPlayer()) {
        for (const upIndx in userRights.userPlayer) {
          userRights.userPlayer[upIndx] = true
        }
      }

      // User has user_schedule group
      if (this.hasGroupUserSchedule()) {
        for (const usIndx in userRights.userSchedule) {
          userRights.userSchedule[usIndx] = true
        }
      }

      // User has user_alert group
      if (this.hasGroupUserAlert()) {
        // Enable alert
        for (const usIndx in userRights.userAlert) {
          userRights.userAlert[usIndx] = true
        }

        /**
         * - Enable Edit project template
         * - Enable Delete media
         * - Enable upload media
        */
        for (const ucIndx in userRights.userCreate) {
          if (ucIndx === 'editProject') userRights.userCreate[ucIndx] = true
          if (ucIndx === 'uploadMedia') userRights.userCreate[ucIndx] = true
          if (ucIndx === 'deleteMedia') userRights.userCreate[ucIndx] = true
        }

        /**
         * - Enable schedule to change target
        */
        for (const ucIndx in userRights.userSchedule) {
          if (ucIndx === 'changeTarget') userRights.userSchedule[ucIndx] = true
        }
      }

      // User has user_create_lock
      if (this.hasGroupUserCreateLock()) {
        userRights.userCreateLock = true

        // Enable user create
        for (const ucIndx in userRights.userCreate) {
          userRights.userCreate[ucIndx] = true

          if (ucIndx === 'createCustomProject') userRights.userCreate[ucIndx] = false
        }
      }

      // User has schedule rights but no create rights nor not lock create
      if (this.hasGroupUserSchedule() && (!this.hasGroupUserCreate() && !this.hasGroupUserCreateLock())) {
        // Disabled user create
        for (const ucIndx in userRights.userCreate) {
          if (ucIndx === 'viewTemplateSets') userRights.userCreate[ucIndx] = false
        }
      }

      return userRights
    }

    return userRights
  },
  getUserDetails () {
    let res = null
    const signInUserSession = this.getUserSessionFromLocalstorage()
    if (signInUserSession?.idToken) {
      res = signInUserSession.idToken.payload

      if (res.identities) {
        res.identityProviderName = res.identities[0].providerName
      }
    }

    const $signedInUserDetails = this.getSignedInUserDetails()
    if (res && $signedInUserDetails) {
      res = { ...res, ...$signedInUserDetails }
    }

    return res
  },
  getJWToken () {
    const signInUserSession = this.getUserSessionFromLocalstorage()
    return signInUserSession?.idToken?.toString() ?? null
  },
  getUsername () {
    if (this.getUserDetails() !== null && this.getUserDetails()['cognito:username']) {
      return this.getUserDetails()['cognito:username']
    }

    return null
  },
  getUserId () {
    const hasCustomUserId = this.getUserDetails() !== null && this.getUserDetails()['custom:user_id']
    const hasSub = this.getUserDetails() !== null && this.getUserDetails().sub

    if (hasCustomUserId) {
      return this.getUserDetails()['custom:user_id']
    } else if (!hasCustomUserId && hasSub) {
      return this.getUserDetails().sub
    }

    return null
  },
  /**
   * standAloneUser is a user without history of purchasing a plan
  */
  isStandAloneUser () {
    if (this.getUserSessionFromLocalstorage()) {
      const currentUserGroups = this.getUserGroups()
      if (currentUserGroups.indexOf('extended') < 0 && this.getUserGroupId() === null) {
        return true
      }
    }
    return false
  },
  /**
   * Free user is a user with history of purchasing a plan AND downgraded from extended to free plan
  */
  isFreeUser () {
    if (this.getUserSessionFromLocalstorage()) {
      const currentUserGroups = this.getUserGroups()

      if (currentUserGroups.indexOf('extended') < 0 && this.getUserGroupId() !== null) {
        return true
      }
    }

    return false
  },
  /**
   * Check that user is a Premium user by exclusion
   */
  isPremiumUser () {
    return this.hasGroupExtended() && !this.hasGroupEnterprise()
  },
  /**
   * Check if user is an Enterprise user
   */
  isEnterpriseUser () {
    return this.hasGroupExtended() && this.hasGroupEnterprise()
  },
  isSubUser () {
    // Instead of checking with existing getUserGroupId to
    // be a subUser, check getUserGroups if it has atleast one item
    if (this.getUserSessionFromLocalstorage() && this.getUserGroups().length > 0) {
      return true
    }
    return false
  },
  isUnleash () {
    return this.hasGroupExtended() && (!this.hasGroupEnterprise() || this.hasGroupUnleash())
  },
  hasFeatureElementiMedia () {
    if (this.getUserGroups().length >= 0 && this.getUserGroups().indexOf('enterprise') >= 0) {
      return true
    }

    return false
  },
  hasFeatureProofOfPlay () {
    if (this.getAccountFeatures().proofOfPlay) {
      useMixpanel().register({ proof_of_play: true })
      return true
    }

    return false
  },
  hasFeatureProofOfPlayBasic () {
    if (this.getAccountFeatures().proofOfPlay === 'basic') {
      useMixpanel().register({ proof_of_play_basic: true })
      return true
    }

    return false
  },
  hasFeatureProofOfPlayExtended () {
    if (this.getAccountFeatures().proofOfPlay === 'extended') {
      useMixpanel().register({ proof_of_play_extended: true })
      return true
    }

    return false
  },
  hasFeatureCustomRSS () {
    return !!this.getAccountFeatures()?.custom_rss
  },
  hasGroupGlobalAdmin () {
    if (this.getUserGroups().indexOf('account_admin') >= 0) {
      return true
    }

    return false
  },
  hasGroupUserAdmin () {
    if (this.getUserGroups().indexOf('user_admin') >= 0) {
      return true
    }
    return false
  },
  hasGroupUserBilling () {
    if (this.getUserGroups().indexOf('user_billing') >= 0) {
      return true
    }
    return false
  },
  hasUserRightModify () {
    return !this.hasGroupExtended() || this.hasGroupUserCreateLock() || this.hasGroupUserCreate() || this.hasGroupUserAdmin()
  },
  hasUserRightCreate () {
    if (!this.hasGroupExtended()) return false
    return this.hasGroupUserCreate() || this.hasGroupUserAdmin()
  },
  hasUserRightAlert () {
    if (!this.hasGroupExtended()) return false
    return this.hasGroupUserAlert() || this.hasGroupUserAdmin()
  },
  hasUserRightSchedule () {
    return !this.hasGroupExtended() || this.hasGroupUserSchedule() || this.hasGroupUserAdmin()
  },
  hasUserRightPlayer () {
    return !this.hasGroupExtended() || this.hasGroupUserPlayer() || this.hasGroupUserAdmin()
  },
  hasUserRightMonitoring () {
    return this.hasGroupExtended() && (this.hasGroupUserPlayer() || this.hasGroupUserAdmin())
  },
  hasUserRightSmartText () {
    return this.hasGroupExtended() && (this.hasGroupUserCreateLock() || this.hasGroupUserCreate() || this.hasGroupUserAdmin())
  },
  hasUserRightReporting () {
    return this.hasGroupExtended() && (this.hasGroupUserPlayer() || this.hasGroupUserAdmin())
  },
  hasUserRightAdmin () {
    return this.hasGroupUserAdmin()
  },
  hasGroupUserCreate () {
    if (this.getUserGroups().length >= 0 && this.getUserGroups().indexOf('user_create') >= 0) {
      return true
    }
    return false
  },
  hasGroupUserCreateLock () {
    if (this.getUserGroups().length >= 0 && this.getUserGroups().indexOf('user_create_lock') >= 0) {
      return true
    }
    return false
  },
  hasGroupUserAlert () {
    if (this.getUserGroups().length >= 0 && this.getUserGroups().indexOf('user_alert') >= 0) {
      return true
    }
    return false
  },
  hasGroupUserPlayer () {
    if (this.getUserGroups().length >= 0 && this.getUserGroups().indexOf('user_player') >= 0) {
      return true
    }
    return false
  },
  hasGroupUserSchedule () {
    if (this.getUserGroups().length >= 0 && this.getUserGroups().indexOf('user_schedule') >= 0) {
      return true
    }
    return false
  },
  hasGroupExtended () {
    if (this.getUserGroups().length >= 0 && this.getUserGroups().indexOf('extended') >= 0) {
      useMixpanel().register({ extended: true })
      return true
    }
    return false
  },
  hasGroupDevelopment () {
    if (this.getUserGroups().length >= 0 && this.getUserGroups().indexOf('development') >= 0) {
      useMixpanel().register({ development: true })
      return true
    }
    return false
  },
  hasGroupDebug () {
    if (this.getUserGroups().length >= 0 && this.getUserGroups().indexOf('debug') >= 0) {
      useMixpanel().register({ development: true })
      return true
    }
    return false
  },
  hasGroupDistributor () {
    if (this.getUserGroups().length >= 0 && this.getUserGroups().indexOf('distributor') >= 0) {
      return true
    }
    return false
  },
  hasGroupTagsUser () {
    if ((this.getUserGroups().length >= 0 && this.getUserGroups().indexOf('tags_user') >= 0) || this.hasGroupEnterprise()) {
      return true
    }
    return false
  },
  hasGroupEnterprise () {
    if (this.getUserGroups().length >= 0 && this.getUserGroups().indexOf('enterprise') >= 0) {
      return true
    }
    return false
  },
  hasGroupUnleash () {
    if (this.getUserGroups().length >= 0 && this.getUserGroups().indexOf('unleash') >= 0) {
      return true
    }
    return false
  },
  hasGroupDealerLED () {
    if (this.getUserGroups().length >= 0 && this.getUserGroups().indexOf('dealer_led') >= 0) {
      return true
    }
    return false
  },
  hasGroupUserLimited () {
    if (this.getUserGroups().length >= 0 && this.getUserGroups().indexOf('user_limited') >= 0) {
      return true
    }
    return false
  },
  getUserInfoFromBackend (params) {
    const getUserInfoRequest = () => {
      // Inject $api dependency
      const $api = this.getApiInstance()
      EventBus.$emit('MAIN_LOADER_SHOW')

      $api.get(`${CONFIG.API_BASE_URL}/group/info`)
        .then((response) => {
          const userInfo = this.filterUserInfoFromBackend(response.data)

          if (typeof (params) !== 'undefined' && params.callback) {
            params.callback(userInfo)
          }
        })
        .catch((error) => {
          ApiError.handleAPIRequestError(error, { originalRequest: () => { getUserInfoRequest() }, showDialogNotification: true })
        })
        .finally(() => {
          EventBus.$emit('MAIN_LOADER_HIDE')
        })
    }
    getUserInfoRequest()
  },
  getMultipleAccountsFromBackend (params) {
    const getMultipleAccounts = () => {
      // Inject $api dependency
      const $api = this.getApiInstance()
      EventBus.$emit('MAIN_LOADER_SHOW')

      $api.get(`${CONFIG.API_BASE_URL}/group/accounts`)
        .then((response) => {
          const userInfo = response.data.accounts

          if (typeof (params) !== 'undefined' && params.callback) {
            params.callback(userInfo)
          }
        })
        .catch((error) => {
          ApiError.handleAPIRequestError(error, { originalRequest: () => { getMultipleAccounts() }, showDialogNotification: true })
        })
        .finally(() => {
          EventBus.$emit('MAIN_LOADER_HIDE')
        })
    }
    getMultipleAccounts()
  },
  filterUserInfoFromBackend (userInfoSrc) {
    const userInfo = Utils.clone(userInfoSrc)

    if (!userInfo._edit) {
      userInfo._edit = {
        plan: {
          currentPlan: ''
        }
      }
    }

    userInfo._edit.hasAddressInfo = false
    userInfo._edit.hasCreditCardInfo = false

    // Set address hasInfo
    let countAddressInfo = 0
    if (userInfo.ccInfo) {
      for (const addInfoIndx in userInfo.address) {
        if (userInfo.address[addInfoIndx]) {
          countAddressInfo += 1
        }
      }
    }
    userInfo._edit.hasAddressInfo = (countAddressInfo > 2) // 2 properties are default, firstName and lastname

    // Set Credit Card hasInfo
    let countCCInfo = 0
    if (userInfo.ccInfo) {
      for (const indx in userInfo.ccInfo) {
        if (userInfo.ccInfo[indx]) {
          countCCInfo += 1
        }
      }
    }

    userInfo._edit.hasCreditCardInfo = (countCCInfo > 1)

    // Set current plan info
    const discoveryPlan = Utils.options.plans.DISCOVERY
    userInfo._edit.plan = {
      showDiscoveryCard: true,
      showPremiumCard: true,
      currentPlan: discoveryPlan,
      planDescription: 'no plan history'
    }

    if (userInfo.plans && userInfo.plans.length) {
      const currentPlan = userInfo.activePlan

      if (currentPlan.endDate) { // Current plan is in discovery mode but the extended(as premium) group user rights will be remove based from endDate
        userInfo._edit.plan.showDiscoveryCard = true
        userInfo._edit.plan.showPremiumCard = true
        userInfo._edit.plan.currentPlan = discoveryPlan
        userInfo._edit.plan.planDescription = `User switched to ${discoveryPlan} but all features under ${currentPlan.name.toUpperCase()} is valid until ${moment(currentPlan.endDate).format('MMM DD YYYY')}`
      } else { // Current plan is premium or enterprise and no endDate
        userInfo._edit.plan.showDiscoveryCard = false
        userInfo._edit.plan.showPremiumCard = userInfo.activePlan.name === Utils.options.plans.PREMIUM // Show premiumCard in premium plan
        userInfo._edit.plan.currentPlan = userInfo.activePlan.name
        userInfo._edit.plan.planDescription = 'no plan history'
      }
    }

    // Set plan name from bundled plan
    userInfo._edit.plan.currentPlanName = Utils.getPlanNameFromBundledPlan(userInfo._edit.plan.currentPlan)

    return userInfo
  },

  /**
   * Get Product Entitlements from user session payload and return list products
   * @returns {string[]} List of product entitlements
  */
  getProductEntitlements () {
    const signInUserSession = this.getUserSessionFromLocalstorage()
    if (!signInUserSession?.idToken?.payload?.entitlements) {
      return []
    }

    try {
      return JSON.parse(signInUserSession.idToken.payload.entitlements)
    } catch (e) {
      console.error('Error parsing entitlements:', e)
      return []
    }
  },
  /**
   * Check if user has entitlement for a specific product
   * @param {string} productName - Name of the product to check entitlement for
   * @returns {boolean} True if user has entitlement for the product, false otherwise
   */
  hasProductEntitlement (productName) {
    const entitlements = this.getProductEntitlements() ?? []
    return entitlements.indexOf(productName) >= 0
  },
  /**
   * Determine if the user can access the product page they are currently on
   * @returns {boolean} True if user has access to selected product, false otherwise
   */
  hasAccessToSelectedProduct () {
    // Determine the selected product based on the current page
    const selectedProduct = Utils.isPageForLicenses()
      ? CONSTANTS.PRODUCT_ACTIVATE
      : CONSTANTS.PRODUCT_ARYA

    return this.hasProductEntitlement(selectedProduct)
  }
}
